import gsap from 'gsap';
import { Graphics } from 'pixi.js';
import { gameConfig } from '../config/gameConfig.js';

/**
 * Traveling gold pulse along a payline before symbol win clips fire.
 * @param {import('pixi.js').Container} lineRoot
 * @param {{ x: number, y: number }[]} pts
 * @param {import('../animation/AnimationController.js').AnimationController} animationController
 * @param {number} [durationSec] override config duration
 * @returns {Promise<void>}
 */
export function animatePaylineEnergy(lineRoot, pts, animationController, durationSec) {
  if (!lineRoot || !Array.isArray(pts) || pts.length < 2) {
    return Promise.resolve();
  }

  const fx = gameConfig.visualFx ?? {};
  const duration =
    durationSec != null && Number.isFinite(durationSec)
      ? durationSec
      : (fx.paylineEnergyDuration ?? 0.38);
  if (duration <= 0) return Promise.resolve();
  const color = gameConfig.theme?.goldBright ?? 0xfff8dc;

  const dot = new Graphics();
  dot.circle(0, 0, fx.paylineEnergyDotRadius ?? 9).fill({ color, alpha: 0.95 });
  dot.circle(0, 0, (fx.paylineEnergyDotRadius ?? 9) + 5).stroke({
    width: 3,
    color: gameConfig.theme?.gold ?? 0xe7c463,
    alpha: 0.65,
  });
  dot.eventMode = 'none';
  lineRoot.addChild(dot);

  const trail = new Graphics();
  trail.eventMode = 'none';
  lineRoot.addChild(trail);

  /** Cumulative segment lengths */
  const segLens = [];
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    const d = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
    segLens.push(d);
    total += d;
  }
  if (total <= 0) {
    dot.destroy();
    trail.destroy();
    return Promise.resolve();
  }

  const sampleAt = (t) => {
    const dist = t * total;
    let acc = 0;
    for (let i = 0; i < segLens.length; i++) {
      const seg = segLens[i];
      if (acc + seg >= dist) {
        const local = (dist - acc) / seg;
        const a = pts[i];
        const b = pts[i + 1];
        return {
          x: a.x + (b.x - a.x) * local,
          y: a.y + (b.y - a.y) * local,
        };
      }
      acc += seg;
    }
    return pts[pts.length - 1];
  };

  const state = { t: 0 };
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      animationController.releaseTween(tw);
      if (!dot.destroyed) {
        dot.parent?.removeChild(dot);
        dot.destroy();
      }
      if (!trail.destroyed) {
        trail.parent?.removeChild(trail);
        trail.destroy();
      }
      resolve();
    };
    const tw = gsap.to(state, {
      t: 1,
      duration,
      ease: 'power2.inOut',
      onUpdate: () => {
        if (dot.destroyed || trail.destroyed || !dot.position || !trail.position) {
          tw.kill();
          return;
        }
        const p = sampleAt(state.t);
        dot.position.set(p.x, p.y);
        const tailT = Math.max(0, state.t - 0.12);
        const tail = sampleAt(tailT);
        trail.clear();
        trail.moveTo(tail.x, tail.y);
        trail.lineTo(p.x, p.y);
        trail.stroke({
          width: fx.paylineEnergyTrailWidth ?? 6,
          color,
          alpha: 0.55,
          cap: 'round',
        });
      },
      onComplete: finish,
      onInterrupt: finish,
    });
    lineRoot.__energyTween = tw;
    animationController.trackTween(tw);
  });
}
